import { defineStore } from 'pinia';

export const useCoursesStore = defineStore('courses', {
    state: () => ({
        course: {
            sections: [],
        },
        section: null,
        chapter: null,
        currentChapter: null,
        loadingCourse: false,
        error: null,
        courseSidebarOpen: false,
        previousProgress: 0,
    }),
    getters: {
        chapters: (state) => {
            let chapters = [];
            state.course?.sections?.forEach((section) => {
                section.chapters.forEach((chapter) => {
                    chapters.push(chapter);
                });
            });

            return chapters;
        },
        progress: (state) => {
            const totalComplete = state.course?.sections?.reduce(
                (count, section) => {
                    const chapterSum = section.chapters.reduce(
                        (chapterAcc, chapter) => {
                            return (
                                chapterAcc +
                                (chapter.user_status === 'complete' ? 1 : 0)
                            );
                        },
                        0
                    );
                    return count + chapterSum;
                },
                0
            );

            return Math.round((totalComplete / state.chapters.length) * 100);
        },
        startNextChapter: (state) => {
            const newChapter = state.chapters.find((chapter) => {
                return chapter.user_status === 'new';
            });

            if (newChapter) {
                return newChapter;
            }

            return null;
        },
        finishChapter: (state) => {
            const leftOverChapter = state.chapters.find((chapter) => {
                return chapter.user_status === 'started';
            });

            if (leftOverChapter) {
                return leftOverChapter;
            }

            return null;
        },
        nextChapter: (state) => {
            const chapterIndex = state.chapters.indexOf(state.currentChapter);

            const leftOverChapter = state.chapters.find((chapter) => {
                return chapter.user_status === 'started';
            });

            if (state.chapters[chapterIndex + 1]) {
                return state.chapters[chapterIndex + 1];
            }

            if (leftOverChapter) {
                return leftOverChapter;
            }

            return null;
        },
        previousChapter: (state) => {
            const chapterIndex = state.chapters.indexOf(state.currentChapter);
            return state.chapters[chapterIndex - 1];
        },
    },
    actions: {
        async getFirstCourse(slug) {
            this.setLoadingCourse(true);
            return ApiService.apiCall('/courses/first-course/', 'get')
                .then((response) => {
                    this.setLoadingCourse(false);
                    this.course = response.data.course;
                    return response.data;
                })
                .catch((e) => {
                    this.setLoadingCourse(false);
                    throw new Error('Error getting course. ' + e.message);
                });
        },
        async getCourse(slug) {
            if (this.course.sections.length !== 0) {
                return;
            }
            this.setLoadingCourse(true);
            return ApiService.apiCall('/course', 'get')
                .then((response) => {
                    this.setLoadingCourse(false);
                    this.course = response.data.course;
                    return response.data;
                })
                .catch((e) => {
                    this.setLoadingCourse(false);
                    throw new Error('Error getting course. ' + e.message);
                });
        },
        async getChapter(slug, courseSlug = null) {
            if (this.course.sections.length === 0) {
                await this.getCourse(courseSlug);
            }

            this.course.sections.forEach((section) => {
                section.chapters.find((chapter) => {
                    if (chapter.slug === slug) {
                        this.section = section;
                        this.chapter = chapter;
                    }
                });
            });
        },
        async startChapter(chapterUuid) {
            return ApiService.apiCall(
                `/course/chapters/${chapterUuid}/start`,
                'get'
            )
                .then((response) => {
                    this.setChapterStatus(chapterUuid, 'started');
                    return response.data;
                })
                .catch((e) => {
                    this.setLoadingCourse(false);
                    throw new Error('Error starting chapter. ' + e.message);
                });
        },
        async completeChapter(chapterUuid) {
            return ApiService.apiCall(
                `/course/chapters/${chapterUuid}/complete`,
                'get'
            )
                .then((response) => {
                    this.setChapterStatus(chapterUuid, 'complete');
                    return response.data;
                })
                .catch((e) => {
                    this.setLoadingCourse(false);
                    throw new Error(
                        'Error marking chapter complete. ' + e.message
                    );
                });
        },
        async restartChapter(chapterUuid) {
            return ApiService.apiCall(
                `/course/chapters/${chapterUuid}`,
                'delete'
            )
                .then((response) => {
                    this.setChapterStatus(chapterUuid, 'new');
                    return response.data;
                })
                .catch(() => {
                    this.setLoadingCourse(false);
                    throw new Error('Error restarting chapter');
                });
        },
        setError(val) {
            this.error = val;
        },
        setCurrentChapter(chapter) {
            this.currentChapter = chapter;
        },
        setChapterStatus(uuid, status) {
            this.course.sections.forEach((section) => {
                section.chapters.forEach((chapter) => {
                    if (chapter.uuid === uuid) {
                        chapter.user_status = status;
                    }
                });
            });
        },
        setLoadingCourse(val) {
            this.loadingCourse = val;
        },
        setCourseSidebarOpen() {
            this.courseSidebarOpen = !this.courseSidebarOpen;
        },
    },
});
